import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { referralLink, referralRewardCopy } from '../utils/referrals';
import { phiTrack } from '../utils/phiTrack';
import { recordReferralNavOpen } from '../utils/referralTracking';
import ReferralShareActions from './ReferralShareActions';
import ReferralHandleForm from './ReferralHandleForm';

const seenKey = (uid) => `cfa_nav_refer_seen_${uid}`;
const readSeen = (uid) => { try { return window.localStorage.getItem(seenKey(uid)) === '1'; } catch { return true; } };
const SHEET_QUERY = '(max-width: 899px)';
const isSheet = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(SHEET_QUERY).matches;
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function GiftIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="8" width="18" height="4" rx="1" />
      <path d="M12 8v13M19 12v8a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-8" />
      <path d="M12 8H7.5a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8zM12 8h4.5a2.5 2.5 0 0 0 0-5C13 3 12 8 12 8z" />
    </svg>
  );
}

/** Short, honest navbar label: the per-friend credit while it can still be earned, else "Refer". */
export function navReferLabel(data) {
  const credit = data?.friendCredit;
  if (credit && !(data.creditAvailableUsd > 0) && (Number(data.creditEarnedUsd) || 0) < credit.capUsd) {
    return `Get $${credit.perFriendUsd}`;
  }
  return 'Refer';
}

/**
 * Gift button left of the account avatar + the "Refer a friend" panel (anchored dropdown on
 * desktop, bottom sheet on small screens). `open` is owned by the navbar so the mobile drawer's
 * "Refer a friend" item can open the same panel. `summary` comes from useReferralSummary in the
 * navbar (one fetch, cached). Renders nothing while referrals are off.
 */
export default function NavReferral({ user, summary, open, onOpenChange }) {
  const { data, setData } = summary;
  const [seen, setSeen] = useState(() => readSeen(user.uid));
  const [notice, setNotice] = useState('');
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  // Small screens get a bottom sheet portalled to <body>: the navbar's backdrop-filter would
  // otherwise make position:fixed relative to the navbar (same reason the site drawer is portalled).
  const [sheet, setSheet] = useState(isSheet);
  useEffect(() => {
    if (!open || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(SHEET_QUERY);
    const sync = () => setSheet(mq.matches);
    sync();
    mq.addEventListener?.('change', sync);
    return () => mq.removeEventListener?.('change', sync);
  }, [open]);

  useEffect(() => { setSeen(readSeen(user.uid)); }, [user.uid]);

  useEffect(() => {
    if (!open) return undefined;
    if (!seen) {
      try { window.localStorage.setItem(seenKey(user.uid), '1'); } catch { /* storage off */ }
      setSeen(true);
    }
    const panel = panelRef.current;
    panel?.querySelector(FOCUSABLE)?.focus();
    const close = () => { onOpenChange(false); buttonRef.current?.focus(); };
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key !== 'Tab' || !panel) return;
      const items = [...panel.querySelectorAll(FOCUSABLE)];
      if (!items.length) return;
      const first = items[0]; const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    const onDown = (e) => {
      if (!wrapRef.current?.contains(e.target) && !panelRef.current?.contains(e.target)) onOpenChange(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open, seen, user.uid, onOpenChange, sheet]);

  if (!data?.enabled) return null;

  const hasCredit = data.creditAvailableUsd > 0;
  const label = navReferLabel(data);
  const toggle = () => {
    if (!open) phiTrack('referral_nav_open', { source: 'button', placement: 'button' });
    if (!open) recordReferralNavOpen('button');
    onOpenChange(!open);
  };
  const link = data.verificationRequired ? '' : referralLink(window.location.origin, data);
  const copy = referralRewardCopy(data);

  const panel = (
        <>
          <button type="button" className="nav-refer-backdrop" aria-label="Close" tabIndex={-1} onClick={() => onOpenChange(false)} />
          <div ref={panelRef} className="nav-refer-panel site-ui" role="dialog" aria-modal="true" aria-label="Refer a friend">
            <div className="nav-refer-head">
              <h2>{copy.headline}</h2>
              <button type="button" className="nav-refer-close" aria-label="Close" onClick={() => { onOpenChange(false); buttonRef.current?.focus(); }}>×</button>
            </div>
            {data.verificationRequired ? (
              <p>Verify your email to get your referral link. <Link to="/dashboard#referrals" onClick={() => onOpenChange(false)}>Verify on your dashboard →</Link></p>
            ) : (
              <>
                {copy.lines.length ? <ul className="referral-reward-lines">{copy.lines.map(line => <li key={line}>{line}</li>)}</ul>
                  : <p>Friends save 10% on any lifetime plan through your link, and you earn 10% of what they pay.</p>}
                {hasCredit ? <p className="referral-balance"><strong>Credit available: ${data.creditAvailableUsd}</strong> · applied at lifetime checkout</p> : null}
                <label className="nav-refer-link">Your link<input aria-label="Your referral link" readOnly value={link} onFocus={e => e.target.select()} /></label>
                <ReferralShareActions link={link} source="nav" onNotice={setNotice} />
                <p role="status" className="nav-refer-notice">{notice}</p>
                {data.handle ? null : <ReferralHandleForm onNotice={setNotice} onSaved={handle => setData({ ...data, handle })} />}
              </>
            )}
            <Link className="nav-refer-more" to="/dashboard#referrals" onClick={() => onOpenChange(false)}>See your referral stats →</Link>
          </div>
        </>
  );

  return (
    <div className="nav-refer" ref={wrapRef}>
      <button ref={buttonRef} type="button" className="nav-refer-btn" aria-label="Refer a friend" title="Refer a friend"
        aria-expanded={open} aria-haspopup="dialog" onClick={toggle}>
        <GiftIcon />
        <span className="nav-refer-label" aria-hidden="true">{label}</span>
        {!seen || hasCredit ? <span className="nav-refer-dot" data-testid="nav-refer-dot" aria-hidden="true" /> : null}
      </button>
      {open ? (sheet ? createPortal(<div className="nav-refer-sheet">{panel}</div>, document.body) : panel) : null}
    </div>
  );
}
